/**
 * 値が plain object かを判定する。
 *
 * @remarks
 * transport decode や spec の read-side では、配列や null を早めに弾いて
 * 曖昧な shape を fail-fast に扱いたい。最低限の object guard を共通化する。
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * 候補から先頭の非空文字列を返す。
 *
 * @remarks
 * prompt / narrative / UI title の候補が複数あるとき、空文字を truthy 扱いせず
 * 同じ規則で最終表示文言を選びたい場面に使う。
 */
export function resolveNonBlankText(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const trimmed = candidate.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return null;
}

/**
 * camelCase の区切りを `_` に展開する。
 *
 * @remarks
 * adapter 由来の field key から snake/kebab alias を作る前処理として使い、
 * lowerCamelCase の境界だけを可逆に近い形で分離する。
 */
export function splitCamelCase(value: string): string {
  if (value.length <= 1) return value;
  return value.replace(/([a-z0-9])([A-Z])/g, "$1_$2");
}

/**
 * 数値らしい入力を有限 number へ寄せる。
 *
 * @remarks
 * adapter / narration / chart logic が同じ数値 coercion 規則を共有し、
 * `1,234` のような表示寄り文字列も read-side で安全に扱えるようにする。
 */
export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.split(",").join(""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
